import AppView from '../../app-view';
import BotListContent from './components/BotListContent';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Plus } from 'lucide-react';
import { setIsReadyToLoadFlow } from '@common/reducers/ChatbotReducer';
import { createAndReRouteToNewBotEditorThunk } from '@common/reducers/ChatbotThunks';
import { botSelectByQuery } from '@common-reducers/DBServiceThunks';
import { AnalyticsService } from '@common/services/analytics-service';
import { useCallback, useState } from 'react';
import BTButton from '../../bt-button/bt-button';

export default function BotListView() {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const allBots = useAppSelector((state) => botSelectByQuery(state.DBBotReducer, {}));
    const analytics = new AnalyticsService();
    const [isCreating, setIsCreating] = useState(false);

    const handleCreateNewBot = useCallback(async () => {
        // Guard against double-clicks / re-entrant creation producing duplicate
        // bots (FE #185). The button is also disabled while `isCreating`.
        if (isCreating) return;
        setIsCreating(true);

        // Track new bot creation
        analytics.event('bot_creation_initiated', {
            category: 'bot_management',
            action: 'create_new_bot',
            total_bots: allBots.length,
            published_bots: allBots.filter(bot => bot.activeDeploymentId).length
        });

        try {
            await dispatch(setIsReadyToLoadFlow(false));
            await dispatch(createAndReRouteToNewBotEditorThunk({ navigate }));
        } finally {
            setIsCreating(false);
        }
    }, [dispatch, navigate, analytics, allBots, isCreating]);

    // The knowledge base has no nav entry of its own — it serves these agents,
    // so it is managed from here.
    const headerActions = (
        <div className="flex items-center gap-2">
            <BTButton
                onClick={() => navigate('/knowledge-base')}
                startIcon={<BookOpen className="w-5 h-5" />}
                btvariant="secondary"
            >
                {t('agents.manageKnowledgeBase')}
            </BTButton>
            <BTButton
                onClick={handleCreateNewBot}
                disabled={isCreating}
                startIcon={<Plus className="w-5 h-5" />}
                btvariant="primary"
            >
                {t('agents.newAgent')}
            </BTButton>
        </div>
    );

    return (
        <AppView 
            title={t('agents.title')} 
            activeExtensionView="bots" 
            showHeader={true}
            headerActions={headerActions}
            showStatusItems={false}
        >
            <BotListContent />
        </AppView>
    );
} 